<template>
  <div class="controls-bar">
    <DatasetFilter
      v-model="datasetFilters"
      :dataset="currentDataset"
      :annotationSets="annotationSets"
      :labels="currentLabels"
      :types="currentTypes"
      :collapsible="true"
      :showCopyFilteredImagesBtn="permissions && permissions.dataset_write"
      :showDeleteFilteredImagesBtn="permissions && permissions.dataset_write"
      @open-copy-filters="openCopyFilteredImagesModal"
      @delete-filtered-images="openConfirmDeleteFilteredImagesModal"
    />
  </div>
  <GalleryGrid
    ref="galleryGrid"
    :selectedImages="selectedImages"
    :imageList="imageList"
    :totalImageCount="totalImageCount"
    :imagesPerPage="imagesPerPage"
    :annotationSets="annotationSets"
    :doneFetch="doneFetch"
  >
    <template #header-first-row-start>
      <div class="gallery-actions">
        <span>{{ selectedImages && selectedImages.length > 0 ? `Selected Images (${selectedImages.length} / ${totalImageCount})` :`Images (${totalImageCount})` }}</span>
      </div>
    </template>
    <template #header-first-row-end>
      <div class="gallery-actions">
        <button v-if="permissions?.dataset_write" class="button" @click.stop="handleSelectAllPageFiles"> {{ !selectAllFiles && !selectAllPageFiles ? 'Select All' : 'Deselect All' }}</button>
        <DisplayAnnotationsFilter
          v-model="annotationDisplaySettings"
          :annotationSets="annotationSets"
          :labels="currentLabels"
        />
        <DisplayImageDetails
          v-model="imageDisplaySettings"
        />
        <GalleryActionsDropdown
          v-if="permissions?.dataset_write"
          :dataset="currentDataset"
          :selectedImages="selectedImages"
          :selectAll="selectAllFiles"
          @remove-tag="handleRemoveTagsForSelectedImages"
          @add-tag="handleAddTagsForSelectedImages"
          @delete-images="openConfirmDeleteImageModal"
          @refresh-dataset="handleRefreshPage"
          @copy-image-modal="() => showCopyModal = true"
        />
        <button v-if="canWrite" class="button import" @click.stop="openUploadModal">
          <SVGIcon
            :iconName="'import'"
            :width="'18px'"
            :height="'18px'"
          />Import
        </button>
      </div>
    </template>
    <template #header-second-row-start>
      <div class="gallery-actions">
        <!-- <button class="button" :class="{ 'inactive': false }">
            Copy Images
          </button> -->
      </div>
    </template>
    <template #header-second-row-end>
      <div class="gallery-actions">
        <v-pagination
          id="gallery-page-selector"
          v-model="currentPage"
          class="pagination"
          :length="numPages"
          :total-visible="7"
          density="compact"
        />
        <div class="sort-by">
          <GalleryImageSort v-model:sortBy="sortBy" v-model:ascend="reverse" />
        </div>
      </div>
    </template>
    <template #header-third-row>
      <div v-if="selectAllPageFiles && !selectAllFiles" class="select-all-warning mt-2">
        <span>All <strong>{{ selectedImages.length }}</strong> files are selected on this page.</span>
        <button v-if="totalImageCount > selectedImages.length" class="button-text" @click="handleSelectAllFiles">Select all {{ totalImageCount }} files</button>
      </div>
      <div v-if="selectAllFiles" class="select-all-warning mt-2">
        <span>All <strong>{{ totalImageCount }}</strong> files are selected.</span>
        <button class="button-text" @click="handleClearSelectAllFiles">Clear Selection</button>
      </div>
    </template>
    <template #grid-item>
      <!-- click.stop to prevent triggering select box on item click -->
      <GalleryGridItem2
        v-for="imageObj in imageList"
        :key="imageObj.id"
        :imageObj="imageObj"
        :annotations="imageObj.annotations"
        :annotationSets="annotationSets"
        :annotationDisplayType="colorAnnotationsBy"
        :filterAnnotationsBySets="displayAnnotationSets"
        :filterAnnotationsByLabelIndexes="displayLabels"
        :labels="currentLabels"
        :showName="showImageName"
        :showTags="showTags"
        :shouldShowSelector="true"
        :selected="selectedImages.some(img => img.id === imageObj.id) || selectAllFiles || selectAllPageFiles"
        v-bind="gridItemDisplayProp"
        @mousedown.stop=""
        @mouseup.stop=""
        @grid-item-clicked="displayImage($event)"
        @delete-image-from-dataset="openConfirmDeleteImageModal"
        @copy="handleCopy"
        @download="handleDownload"
        @update:selected="isSelected => toggleImageSelection(imageObj, isSelected)"
      />
    </template>
    <template #empty-state>
      <GalleryEmptyState
        v-if="!isFetchingImages"
        ref="galleryEmptyState"
        :datasetID="currentDatasetID"
        @import-clicked="$refs.uploadImagesModal.showModal();"
      />
    </template>
    <template #overlay>
      <div v-if="isFetchingImages" id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
      <GalleryDropZone
        ref="galleryEmptyState"
        :datasetID="currentDatasetID"
        :showIcon="imageList && imageList.length > 0"
        @upload-complete="handleImageUploadComplete"
      />
    </template>
    <template #dropzone>
      <GalleryDropZone
        ref="galleryEmptyState"
        :datasetID="currentDatasetID"
        :showIcon="imageList && imageList.length > 0"
        @upload-complete="handleImageUploadComplete"
      />
    </template>
    <!-- <template #selectBox>
      <GalleryGridSelectBox
        :selectedImages="selectedImages"
        @multi-select="handleMultiSelect"
      />
    </template> -->
  </GalleryGrid>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="buttonClass"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <UploadImagesModal
    ref="uploadImagesModal"
    :datasetID="currentDatasetID"
    :annotationSets="annotationSets"
    @upload-complete="handleImageUploadComplete"
    @upload-error="handleUploadError"
    @confirm-duplicate-upload="confirmDuplicateUpload"
  />
  <DatasetCopyModal
    v-if="showCopyModal"
    ref="copyDatasetModal"
    v-model:show="showCopyModal"
    :datasets="datasets"
    :source="currentDataset"
    :filters="imageFilterParams"
    :showFilters="false"
    :lockSource="true"
    :selectedImages="selectedImages"
    @copy-in-progress="handleClearSelectAllFiles"
  />
  <DatasetExportModal
    ref="datasetExportModal"
    :datasets="datasets"
    :sequences="sequences"
    :exportDataset="currentDataset"
  />
</template>

<script>
import DisplayAnnotationsFilter from '@/components/DatasetComponent/GalleryComponent/DisplayAnnotationsFilterV2.vue';
import GalleryImageSort from '@/components/DatasetComponent/GalleryComponent/GalleryImageSort.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import GalleryGrid from '@/components/DatasetComponent/GalleryComponent/GalleryGrid.vue';
import UploadImagesModal from '@/components/DatasetComponent/UploadImagesModal.vue';
import GalleryGridItem2 from '@/components/DatasetComponent/GalleryComponent/GalleryGridItem2.vue';
import GalleryEmptyState from '@/components/DatasetComponent/GalleryComponent/GalleryEmptyState.vue';
import GalleryDropZone from '@/components/DatasetComponent/GalleryComponent/GalleryDropZone.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import DatasetCopyModal from "@/components/DatasetComponent/DatasetManagement/DatasetCopyModal.vue";
import DatasetExportModal from "@/components/DatasetComponent/DatasetManagement/DatasetExportModal.vue";
import DisplayImageDetails from '@/components/DatasetComponent/GalleryComponent/DisplayImageDetails.vue';
import useGallery from '@/composables/annotationTool/useGallery.js';
import DatasetFilter from '@/components/DatasetComponent/DatasetFilters/DatasetFilter.vue';
import GalleryActionsDropdown from "@/components/DatasetComponent/GalleryComponent/GalleryActionsDropdown.vue";
import equal from 'deep-equal';
import useTaskModal from '@/composables/useTaskModal.js';

import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: 'Gallery',
  components: {
    GalleryGrid,
    DatasetFilter,
    DisplayImageDetails,
    DisplayAnnotationsFilter,
    GalleryGridItem2,
    GalleryEmptyState,
    GalleryDropZone,
    UploadImagesModal,
    SVGIcon,
    IconButton,
    ConfirmModal,
    DatasetCopyModal,
    DatasetExportModal,
    GalleryImageSort,
    GalleryActionsDropdown,
  },
  setup() {
    const {
      datasetFilters,
      annotationDisplaySettings,
      imageDisplaySettings,
      reverse,
      combineSequenceFrames,
      sortBy,
      currentDatasetID,
      internalCurrentDataset,
      currentPage,
      imagesPerPage,
      imageList,
      totalImageCount,
      getImagesParams,
      getImagesFilterParams,
      getImages,
      deleteFilteredImages,
      isFetchingImages,
    } = useGallery();
    combineSequenceFrames.value = true;

    const {
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    } = useTaskModal();

    return {
      datasetFilters,
      annotationDisplaySettings,
      imageDisplaySettings,
      reverse,
      sortBy,
      currentDatasetID,
      internalCurrentDataset,
      currentPage,
      imagesPerPage,
      imageList,
      totalImageCount,
      getImagesParams,
      getImagesFilterParams,
      getImages,
      deleteFilteredImages,
      isFetchingImages,
      combineSequenceFrames,
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    };
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      // annotationSets: null,
      imageListPromise: null,
      mapImageList: [],
      totalPagesCount: 0,
      dropHighlighted: false,
      gallerySelection: [],
      confirmMessage: null,
      confirmMessageHeader: null,
      buttonClass: "button-delete",
      buttonText: "",
      confirmFunction: null,
      showCopyModal: false,
      showCopyImageModal: false,
      permissions: null,
      selectedImage: null,
      sequences: [],
      showExportModal: false,
      doneFetch: true,
      selectedImages: [],
      selectAllPageFiles: false,
      selectAllFiles: false,
    };
  },
  computed: {
    canWrite: {
      get() {
        return this.permissions && this.permissions.dataset_write;
      },
    },
    currentProject: {
      get() {
        return this.$store.state.projects.currentProject;
      },
      set(project) {
        this.$store.commit('projects/setCurrentProject', project);
      },
    },
    datasets: {
      get() {
        return this.$store.state.datasets.datasetList;
      },
      set(datasets) {
        this.$store.commit('datasets/setDatasetList', datasets);
      },
    },
    currentDataset: {
      get() {
        return this.$store.state.datasets.currentDataset;
      },
      set(dataset) {
        this.$store.commit('datasets/setCurrentDataset', dataset);
      },
    },
    currentLabels() {
      return this.currentDataset?.labels || [];
    },
    currentTypes() {
      if (this.currentDataset) {
        return this.currentDataset.annotation_types;
      }
      return [];
    },
    annotationSets: {
      get() {
        let sets = [];
        if (this.currentDataset) {
          sets = this.currentDataset?.annotation_sets;
        }
        if (!sets || (sets && sets.length === 0)) {
          this.getAnnotationSetsForDataset(this.currentDataset);
        }
        return sets;
      },
      set(sets) {
        if (this.currentDataset?.id) {
          const index = this.datasets.indexOf(this.datasets.find((d) => d.id === this.currentDataset.id));
          if (index >= 0) {
            this.$store.commit('datasets/setDatasetAnnotationSets', { index, sets });
          }
        }
      },
    },
    numPages() {
      return Math.ceil(this.totalImageCount / this.imagesPerPage);
    },
    getAnnotationSetsParams() {
      if (!this.currentDataset) {
        return null;
      }

      const params = {
        dataset_id: this.currentDataset.id,
      };

      if (this.currentTab === 'annotations') {
        params.include_empty = true;
      }

      return params;
    },
    gridItemDisplayProp() {
      if (!this.datasetFilters?.displayAnnotationsFilter) {
        return {};
      }
      if (this.datasetFilters?.displayAnnotationsFilter?.displayType === 'byAnnotationSets') {
        return { filterAnnotationsBySets: this.datasetFilters.displayAnnotationsFilter.value };
      }
      if (this.datasetFilters?.displayAnnotationsFilter?.displayType === 'byAnnotationLabels') {
        return { filterAnnotationsByLabelIndexes: this.datasetFilters.displayAnnotationsFilter.value };
      }
      return {};
    },
    colorAnnotationsBy() {
      if (this.annotationDisplaySettings?.colorAnnotationsBy?.type) {
        return this.annotationDisplaySettings?.colorAnnotationsBy?.type;
      }
      return 'set';
    },
    displayAnnotationSets() {
      if (this.annotationDisplaySettings?.displayAnnotationSets) {
        return this.annotationDisplaySettings?.displayAnnotationSets.annotation_sets;
      }

      return [];
    },
    displayLabels() {
      if (this.annotationDisplaySettings?.displayLabels) {
        return this.annotationDisplaySettings?.displayLabels.labels;
      }
      return [];
    },
    showImageName() {
      if (this.imageDisplaySettings?.includes('showName')) {
        return true;
      }
      return false;
    },
    showSequences() {
      if (this.imageDisplaySettings?.includes('showSequences')) {
        return true;
      }
      return false;
    },
    showTags() {
      if (this.imageDisplaySettings?.includes('showTags')) {
        return true;
      }
      return false;
    },
    imageParams() {
      return this.getImagesParams();
    },
    imageFilterParams() {
      return this.getImagesFilterParams();
    },
    getImagesCombinedParams() {
      return { ...this.getImagesParams(), ...this.getImagesFilterParams() };
    },
    combinedParams() {
      return {
        // ...this.currentDatasetID,
        ...this.annotationDisplaySettings,
        ...this.imageDisplaySettings,
      };
    },
    minImageNumber() {
      return ((this.currentPage - 1) * this.imagesPerPage) + 1;
    },
    maxImageNumber() {
      const imgListLength = this.imageList ? this.imageList.length : 0;
      return ((this.currentPage - 1) * this.imagesPerPage) + imgListLength;
    },
    isPublic() {
      return this.currentProject.is_public;
    },
  },
  watch: {
    currentDataset: {
      handler() {
        this.currentPage = 1;
        this.currentDatasetID = this.currentDataset ? this.currentDataset.id : 0;
        this.internalCurrentDataset = this.currentDataset;
      },
    },
    getImagesCombinedParams: {
      deep: true,
      async handler(newVal, oldVal) {
        if (!equal(newVal, oldVal)) {
          this.imageListPromise = await new Promise((resolve, reject) => {
            this.doneFetch = false;
            this.getImages().then((imageList) => {
              this.imageList = imageList;
              this.selectedImages = [];
              this.selectAllPageFiles = false;
              this.selectAllFiles = false;
              resolve(imageList);
              this.doneFetch = true;
            }).catch((e) => {
              this.selectedImages = [];
              this.selectAllPageFiles = false;
              this.selectAllFiles = false;
              this.doneFetch = true;
            });
          });
        }
      },
    },
    combinedParams: {
      deep: true,
      async handler(newVal, oldVal) {
        this.$router.replace({
          name: this.$route.name,
          query: {
            ...this.$route.query,
            dataset: this.currentDatasetID,
            image_params: encodeURI(JSON.stringify({
              imagesPerPage: this.imagesPerPage,
              currentPage: this.currentPage > 0 ? this.currentPage : 1,
              sortBy: this.sortBy,
              reverse: this.reverse,
            })),
            filter_params: encodeURI(JSON.stringify(this.datasetFilters)),
            annotation_display: encodeURI(JSON.stringify(this.annotationDisplaySettings)),
            image_display: encodeURI(JSON.stringify(this.imageDisplaySettings)),
          },
          params: this.$route.params,
        });
      },
    },
    getAnnotationSetsParams: {
      deep: true,
      async handler(params) {
        if (params && params !== undefined) {
          this.annotationSets = await this.getAnnotationSetList(params);
        } else {
          this.annotationSets = [];
        }
      },
    },
    showSequences() {
      this.combineSequenceFrames = this.showSequences;
    },
  },
  async mounted() {
    if (this.$route.query.dataset) {
      const idInt = parseInt(this.$route.query.dataset);
      if (this.datasets && this.datasets.length > 0) {
        const d = this.datasets.find((e) => e.id === idInt);
        if (d) {
          this.currentDataset = d;
        }
      } else {
        await this.getDatasetDetail(idInt);
      }
    }

    this.currentDatasetID = this.currentDataset ? this.currentDataset.id : 0;
    this.internalCurrentDataset = this.currentDataset;

    if (this.$route.query.image_params) {
      const image_params = JSON.parse(decodeURI(this.$route.query.image_params));
      this.imagesPerPage = image_params.imagesPerPage;
      this.currentPage = image_params.currentPage;
      this.sortBy = image_params.sortBy;
      this.reverse = image_params.reverse;
    }

    if (this.$route.query.filter_params) {
      this.displayFilters = JSON.parse(decodeURI(this.$route.query.filter_params));
    }

    if (this.$route.query.annotation_display) {
      this.annotationDisplaySettings = JSON.parse(decodeURI(this.$route.query.annotation_display));
    }

    if (this.$route.query.image_display) {
      this.imageDisplaySettings = JSON.parse(decodeURI(this.$route.query.image_display));
    }

    this.GetPermissionsForCurrentUser();

    // this code is to refresh whenever we arrive at gallery page
    this.$nextTick(() => {
      if (this.imageList) {
        this.imageListPromise = new Promise((resolve, reject) => {
          this.doneFetch = false;
          this.getImages().then((imageList) => {
            this.imageList = imageList;
            this.selectedImages = [];
            this.selectAllPageFiles = false;
            this.selectAllFiles = false;
            resolve(imageList);
            this.doneFetch = true;
          }).catch((e) => {
            this.selectedImages = [];
            this.selectAllPageFiles = false;
            this.selectAllFiles = false;
            this.doneFetch = true;
          });
        });
      }
    });
  },
  methods: {
    async getAnnotationSetsForDataset(dataset) {
      if (dataset) {
        const resp = await this.dataConnect.getAnnotationSets({
          dataset_id: dataset.id,
          get_num_annotations: true,
          get_num_images: true,
          get_labels: true,
        })
          .catch((error) => {
            console.log('Failed to retrieve sets:', error);
          });
        if (resp && !resp.error && resp.result) {
          this.annotationSets = resp.result;
        }
      }
    },
    async GetPermissionsForCurrentUser() {
      const user = this.$store.state?.user?.user;
      if (this.currentDataset && user) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentDataset.project_id,
          username: user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            }
          })
          .catch((e) => console.log(e));
      } else {
        this.permissions = null;
      }
    },
    async getAnnotationSetList(params) {
      const annotationSets = await this.dataConnect.getAnnotationSets(params)
        .catch((error) => {
          // TODO: handle error
          console.log(error);
        });
      if (annotationSets.error || !annotationSets.result) {
        // TODO: handle error
        return {};
      }
      return annotationSets.result;
    },
    // async getLabelList() {
    //   const labels = await this.dataConnect.getLabelList({ dataset_id: this.currentDataset.id })
    //     .catch((error) => {
    //       // TODO: handle error
    //       console.log(error);
    //     });
    //   return labels.result;
    // },
    handleGallerySelectionChanged(selection) {
      this.gallerySelection = selection;
    },
    async handleCopyCompleted() {
      this.datasets = await this.getDatasets();
    },
    async handleAnnotationSetCreated() {
      this.annotationSets = await this.getAnnotationSetList();
    },
    openConfirmDeleteFilteredImagesModal() {
      this.confirmMessage = `Are you sure you want to delete all filtered images`;
      this.confirmMessageHeader = 'Delete Filtered Images';
      this.buttonClass = "button-delete";
      this.buttonText = 'Delete';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => this.deleteFilteredImages();
    },
    openConfirmDeleteImageModal() {
      const filtered = this.selectedImages.reduce((acc, item) => {
        if (item.sequence_id && item.sequence_id > 0 && item.type === 'sequence') {
          acc.sequence_ids.push(item.sequence_id);
        } else if (item.id > 0 && item.type === 'image') {
          acc.image_ids.push(item.id);
        } else {
          // also assume non_sequence or non_image to be images
          acc.image_ids.push(item.id);
        }
        return acc;
      }, { sequence_ids: [], image_ids: [] });
      let numImagesToDelete = 0;
      if (this.selectAllFiles) {
        numImagesToDelete = this.totalImageCount;
      } else {
        numImagesToDelete = this.selectedImages.length;
      }
      this.confirmMessage = `Are you sure you want to permanently delete ${numImagesToDelete} item(s)?`;
      this.confirmMessageHeader = `Deleting Items`;
      this.buttonClass = "button-delete";
      this.buttonText = 'Delete';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => this.deleteFilteredImages2(filtered.image_ids, filtered.sequence_ids);
    },
    async deleteFilteredImages2(image_ids, sequence_ids) {
      if (!image_ids.length > 0 && !sequence_ids.length > 0 && !this.selectAllFiles) {
        return;
      }

      const params = {
        ...this.imageFilterParams,
        dataset_id: this.currentDatasetID,
      };

      // Filter by selectect images/sequences
      if (!this.selectAllFiles) {
        if (this.imageFilterParams.images_filter && image_ids.length > 0) {
          this.imageFilterParams.images_filter.image_ids = image_ids;
        }
        if (this.imageFilterParams.images_filter && sequence_ids.length > 0) {
          this.imageFilterParams.images_filter.sequence_ids = sequence_ids;
        }
      }

      this.dataConnect.deleteFilteredImages2(params)
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error.message);
          }
          this.getImages().then((imageList) => {
            this.imageList = imageList;
          });
        })
        .catch((error) => {
          const $toast = useToast();
          $toast.open({
            queue: true,
            duration: 5000,
            message: error.message,
            type: 'error',
            position: 'top',
          });
        });
      this.selectedImages = [];
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    async deleteImageFromDataset(sequence_ids, image_ids) {
      const params = {
        dataset_id: this.currentDataset.id,
        sequence_ids,
        image_ids,
      };
      this.dataConnect.deleteImageFromDataset(params)
        .then((data) => {
          if (!data || data.error || !data.result) {
            const $toast = useToast();
            $toast.open({
              queue: true,
              duration: 5000,
              message: data.error.message,
              type: 'error',
              position: 'top',
            });
          } else {
            this.getImages().then((imageList) => {
              this.imageList = imageList;
            });
          }
        });
      this.selectedImages = [];
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    displayImage(imageObj) {
      this.$router.push({
        name: `${this.isPublic ? 'public.' : ''}datasets.gallery.viewer`,
        query: {
          dataset: this.currentDatasetID,
          image_id: encodeURI(JSON.stringify(imageObj.id)),
          annotationDisplaySettings: encodeURI(JSON.stringify(this.annotationDisplaySettings)),
          showSequences: encodeURI(this.imageDisplaySettings.includes('showSequences')),
        },
      });
    },
    openUploadModal() {
      this.$refs.uploadImagesModal.showModal();
    },
    handleImageUploadComplete() {
      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
      this.$refs.uploadImagesModal.closeModal();
    },
    async handleUploadError(data) {
      this.confirmMessage = data.confirmMessage;
      this.confirmMessageHeader = data.confirmMessageHeader;
      this.buttonText = "";
      this.$refs.confirmModal.showModal();
    },
    openCopyFilteredImagesModal() {
      this.showCopyModal = true;
    },
    async confirmCopy(args) {
      this.confirmMessage = `Destination dataset contains ${args.duplicate_count} ${args.duplicate_count === 1 ? 'image' : 'images'} with the same name. 
      ${args.duplicate_count === 1 ? 'This image' : 'These images'} will be ignored.`;
      this.confirmNote = "";
      this.confirmMessageHeader = 'Duplicate files';
      this.buttonClass = null;
      this.buttonText = "Confirm";
      this.buttonClass = "";
      this.confirmFunction = async () => { await this.handleConfirmCopy(args.fullParams); };
      this.$refs.confirmModal.showModal();
    },
    async handleConfirmCopy(params) {
      await this.dataConnect.confirmCopySequences(params)
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error.message);
          } else if (resp.result) {
            const destDS = this.datasets.find((e) => e.id === params.dest_dataset_id);
            if (!destDS) {
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Copying in progress. Refresh the page to view progress.`,
                type: "success",
                position: "top",
              });
              this.refreshDatasetCard(destDS);
            } else {
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Copying in progress.`,
                type: "success",
                position: "top",
              });
            }

            this.getTasks();
            if (!this.isGettingTasks) {
              this.resumeGetTasks();
            }

            this.showCopyModal = false;
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to copy: ${error.message}`,
            type: "error",
            position: "top",
          });
        });
    },
    handleCopy(imageObj) {
      if (!this.selectedImages.includes(imageObj)) {
        this.selectedImages.push(imageObj);
      }
      this.showCopyModal = true;
    },
    handleDownload(imageObj) {
      if (imageObj.type === 'sequence') {
        this.sequences = [
          {
            "id": imageObj.sequence_id,
            "uuid": imageObj.uuid,
            "name": imageObj.name,
          },
        ];
      }
      this.$refs.datasetExportModal.showModal();
    },
    handleSelectAllPageFiles() {
      if (!this.selectAllPageFiles) {
        this.selectedImages = this.imageList;
        this.selectAllPageFiles = true;
        this.selectAllFiles = false;
      } else {
        this.selectedImages = [];
        this.selectAllPageFiles = false;
        this.selectAllFiles = false;
      }
    },
    handleSelectAllFiles() {
      this.selectAllFiles = true;
      this.selectedImages = [];
    },
    handleClearSelectAllFiles() {
      this.selectedImages = [];
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    toggleImageSelection(imageObj, isSelected) {
      if (isSelected) {
      // Add if not already in
        if (!this.selectedImages.some((img) => img.id === imageObj.id)) {
          this.selectedImages = [...this.selectedImages, imageObj];
        }
      } else {
      // Remove
        this.selectedImages = this.selectedImages.filter(
          (img) => img.id !== imageObj.id,
        );
      }
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    handleMultiSelect(IDs) {
      this.selectedImages = this.imageList.filter((e) => IDs.includes(e.id));
    },
    handleRemoveTagsForSelectedImages(tag_name) {
      const selectedImageIds = this.selectedImages.map((e) => e.id);
      this.imageList.forEach((image, index) => {
        if (selectedImageIds.includes(image.id)) {
          this.imageList[index].tags = this.imageList[index].tags.filter((e) => e.name !== tag_name);
        }
      });
    },
    handleAddTagsForSelectedImages(tag_name) {
      const selectedImageIds = this.selectedImages.map((e) => e.id);
      this.imageList.forEach((image, index) => {
        if (selectedImageIds.includes(image.id)) {
          this.imageList[index].tags.push({ name: tag_name });
        }
      });
    },
    handleRefreshPage() {
      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
    },
    async getDatasetDetail(dataset_id) {
      if (dataset_id) {
        const params = {
          dataset_id,
          get_num_images: true,
          get_labels: true,
          get_num_labels: true,
          get_annotation_sets: true,
          get_groups: true,
          get_docker_tasks: true,
        };
        await this.dataConnect.getDatasetById(params)
          .then((resp) => {
            if (!resp.error) {
              const temp = resp.result;
              this.currentDataset = temp;
            } else {
              let msg = resp.error.message;
              if (msg.includes("no rows")) {
                msg = "Dataset does not exist.";
              }
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Failed to retrieve dataset: ${msg}`,
                type: "error",
                position: "top",
              });
            }
          })
          .catch((error) => {
            let msg = error;
            if (msg.includes("no rows")) {
              msg = "Dataset does not exist.";
            }
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to retrieve dataset: ${msg}`,
              type: "error",
              position: "top",
            });
          });
      }
    },
    async confirmDuplicateUpload(args) {
      this.confirmMessage = `Destination dataset contains ${args.duplicate_count} ${args.duplicate_count === 1 ? 'image' : 'images'} with the same name. 
      ${args.duplicate_count === 1 ? 'This image' : 'These images'} will be ignored.`;
      this.confirmNote = "";
      this.confirmMessageHeader = 'Duplicate files';
      this.buttonText = "Confirm";
      this.buttonClass = null;
      this.confirmFunction = async () => { await this.handleConfirmDuplicateUpload(args.fullParams); };
      this.$refs.confirmModal.showModal();
    },
    async handleConfirmDuplicateUpload(params) {
      this.$refs.uploadImagesModal.handleUploadConfirmed({ ...params, ignoreDuplicate: true });
    },
  },
};
</script>

<style lang="scss" scoped>
.controls-bar {
  display: flex;
  flex-direction: row;
  padding: 10px 20px 10px 20px;
  align-items: center;
  border-bottom: 1px solid #c9c9c9;
  @include themify() {
    background: themed('color-ribbon');
  }
  justify-content: space-between;
}

#dataset-select {
  width: 300px;
}

.gallery-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  height: 100%;

  span{
    font-weight: bold;
    font-size: 1.15rem;
  }

  button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    flex: 1 1 auto;
    padding: 4px 8px;
    gap: 2px;
    border-right: solid 1px rgba(0,0,0,0.5);
    max-width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.import {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }

    &.inactive {
      background: rgba(var(--color-disabled), 0.5) !important;
    }
  }
}

.sort-by {
  display: flex;
  flex-direction: row;
  align-items: center;

  &__select {
    width: 130px;
  }

  &__reverse._invert {
    transform: rotate(180deg);
  }
}

#loading-visualization {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  background: transparent;
}

.lds-ring {
  /* change color here */
  color: var(--color-primary);
}
.lds-ring,
.lds-ring div {
  box-sizing: border-box;
}
.lds-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid currentColor;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: currentColor transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

:deep(#gallery-page-selector button) {
  min-width: fit-content;
}

.select-all-warning {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50px;
  background: var(--gray-300);
  color: var(--gray-600);
  font-weight: 400;
  gap: 12px;

  button {
    color: var(--button-link-color);
  }
}
</style>
